import { useFormatUserDate } from "../core/useFormatUserDate";
import { getProductIcon } from 'legoland-sdk';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFilters, usePagination, useSortBy, useTable } from 'react-table';
import { Box, Button, Input, Modal, RadioGroup, useToasts, useTombac } from 'tombac';
import { AddIcon, DeleteIcon, SearchIcon } from 'tombac-icons';
import { Pagination } from "../contracts/list/Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "../contracts/list/Table";
import reportsClient from "./reportsClient";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ReportsExcludeRecords = _ref => {
  let {
    productOptions
  } = _ref;
  const INITIAL_RECORD = '';
  const [productOption, setProductOption] = useState(productOptions[0]);
  const [excludedRecords, setExcludedRecords] = useState([]);
  const [newExcludedRecord, setNewExcludedRecord] = useState('');
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [activeRecord, setActiveRecord] = useState(INITIAL_RECORD);
  const [filterInput, setFilterInput] = useState('');
  const [pagination, setPagination] = useState({
    index: 0,
    size: 20
  });
  const formatDate = useFormatUserDate({
    withTime: true
  });
  const {
    addToast
  } = useToasts();
  const tombac = useTombac();
  useEffect(() => {
    getExcludedRecords();
  }, [productOption]);
  useEffect(() => {
    gotoPage(pagination.index);
  }, [pagination.index]);
  useEffect(() => {
    setPageSize(pagination.size);
  }, [pagination.size]);
  const onProductOptionChange = option => {
    setProductOption(option);
  };
  const getExcludedRecords = () => {
    reportsClient.getExcludedRecordsList(productOption.value).then(res => setExcludedRecords(res));
  };
  const addNewExcludedRecord = () => {
    reportsClient.addNewExcludedRecord(productOption.value, newExcludedRecord).then(() => {
      getExcludedRecords();
      addToast(`Report ${newExcludedRecord} was added to the excluded list`, 'success');
      setNewExcludedRecord('');
    }).catch(() => {
      addToast('Invalid report id', 'danger');
    });
  };
  const deleteExcludedRecord = recordId => {
    reportsClient.deleteExcludedRecord(productOption.value, recordId).then(() => {
      getExcludedRecords();
      setOpenConfirmationModal(false);
      setActiveRecord(INITIAL_RECORD);
      setNewExcludedRecord('');
      addToast('Report was removed from excluded list', 'success');
    }).catch(() => {
      addToast('Unable to delete report, please try again', 'danger');
    });
  };
  const tableData = useMemo(() => excludedRecords ?? [], [excludedRecords]);
  const columns = useMemo(() => [{
    Header: 'Id',
    accessor: 'id',
    width: 100
  }, {
    Header: 'User',
    accessor: 'user',
    width: 100,
    Cell: _ref2 => {
      let {
        value
      } = _ref2;
      return value?.email ? /*#__PURE__*/_jsx(Link, {
        to: `/users/edit/${encodeURIComponent(value.email)}`,
        children: value.email
      }) : null;
    }
  }, {
    Header: 'Created at',
    accessor: 'createdAt',
    width: 70,
    Cell: _ref3 => {
      let {
        value
      } = _ref3;
      return /*#__PURE__*/_jsx(_Fragment, {
        children: formatDate(new Date(value))
      });
    }
  }, {
    Header: 'Created by',
    accessor: 'createdBy',
    width: 90
  }, {
    Header: 'Product',
    accessor: 'product',
    width: 40,
    Cell: _ref4 => {
      let {
        value
      } = _ref4;
      const Icon = getProductIcon(value.id);
      return /*#__PURE__*/_jsx(Icon, {
        height: "32",
        width: "32"
      });
    }
  }, {
    id: 'id' + 3,
    accessor: row => row.id,
    width: 20,
    Cell: _ref5 => {
      let {
        value
      } = _ref5;
      return /*#__PURE__*/_jsx(Box, {
        $display: "flex",
        $justifyContent: "right",
        children: /*#__PURE__*/_jsx(Button, {
          prepend: /*#__PURE__*/_jsx(DeleteIcon, {}),
          onClick: () => {
            setActiveRecord(value);
            setOpenConfirmationModal(true);
          },
          variant: "flat"
        })
      });
    }
  }], []);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    setFilter,
    gotoPage,
    setPageSize
  } = useTable({
    columns: columns,
    data: tableData,
    autoResetPage: false,
    initialState: {
      sortBy: [{
        id: 'createdAt',
        desc: true
      }]
    }
  }, useFilters, useSortBy, usePagination);
  const onPageChange = page => {
    setPagination(page);
  };
  const handleFilterChange = e => {
    const value = e.target.value || undefined;
    setFilter('id', value);
    setFilterInput(value);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Box, {
      $boxSizing: "content-box",
      $margin: "3sp auto",
      $maxWidth: "1280u",
      $width: "100%",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ReportsExcludeRecords.tsx",
      "data-sentry-element": "Box",
      children: [' ', /*#__PURE__*/_jsx(RadioGroup, {
        onChange: onProductOptionChange,
        options: productOptions,
        variant: "horizontal",
        value: productOption,
        "data-sentry-element": "RadioGroup",
        "data-sentry-source-file": "ReportsExcludeRecords.tsx",
        "data-sentry-element": "RadioGroup"
      }), /*#__PURE__*/_jsx(Box, {
        $mt: "2sp",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "ReportsExcludeRecords.tsx",
        "data-sentry-element": "Box",
        children: /*#__PURE__*/_jsx(Input, {
          $width: "42sp",
          $flex: "1",
          $mb: "2sp",
          onChange: handleFilterChange,
          placeholder: "Search report by id",
          prepend: /*#__PURE__*/_jsx(SearchIcon, {}),
          type: "text",
          value: filterInput,
          "data-sentry-element": "Input",
          "data-sentry-source-file": "ReportsExcludeRecords.tsx",
          "data-sentry-element": "Input"
        })
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $justifyContent: "right",
        $mb: "2sp",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "ReportsExcludeRecords.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(Input, {
          className: "",
          id: "excluded-report-add",
          value: newExcludedRecord,
          placeholder: "Add report exclusion",
          onChange: e => {
            setNewExcludedRecord(e.currentTarget.value);
          },
          "data-sentry-element": "Input",
          "data-sentry-source-file": "ReportsExcludeRecords.tsx",
          "data-sentry-element": "Input"
        }), ' ', /*#__PURE__*/_jsx(Button, {
          $ml: "1sp",
          onClick: addNewExcludedRecord,
          shape: "circle",
          variant: "primary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "ReportsExcludeRecords.tsx",
          "data-sentry-element": "Button",
          children: /*#__PURE__*/_jsx(AddIcon, {
            "data-sentry-element": "AddIcon",
            "data-sentry-source-file": "ReportsExcludeRecords.tsx",
            "data-sentry-element": "AddIcon"
          })
        })]
      }), /*#__PURE__*/_jsx(Pagination, {
        $paddingBottom: "2sp",
        page: pagination,
        itemCount: rows && rows.length,
        onPageChange: page => onPageChange && onPageChange(page),
        "data-sentry-element": "Pagination",
        "data-sentry-source-file": "ReportsExcludeRecords.tsx",
        "data-sentry-element": "Pagination"
      }), /*#__PURE__*/_jsxs(Table, {
        ...getTableProps(),
        "data-sentry-element": "Table",
        "data-sentry-source-file": "ReportsExcludeRecords.tsx",
        "data-sentry-element": "Table",
        children: [/*#__PURE__*/_jsx(Thead, {
          "data-sentry-element": "Thead",
          "data-sentry-source-file": "ReportsExcludeRecords.tsx",
          "data-sentry-element": "Thead",
          children: headerGroups.map(headerGroup => {
            const {
              key: headerGroupKey,
              ...headerGroupProps
            } = headerGroup.getHeaderGroupProps();
            return /*#__PURE__*/_jsx(Tr, {
              ...headerGroupProps,
              children: headerGroup.headers.map(column => {
                const {
                  key: headerKey,
                  ...headerProps
                } = column.getHeaderProps({
                  style: {
                    width: tombac.unit(Number(column.width))
                  }
                });
                return /*#__PURE__*/_jsx(Th, {
                  ...headerProps,
                  children: column.render('Header')
                }, headerKey);
              })
            }, headerGroupKey);
          })
        }), /*#__PURE__*/_jsx(Tbody, {
          ...getTableBodyProps(),
          "data-sentry-element": "Tbody",
          "data-sentry-source-file": "ReportsExcludeRecords.tsx",
          "data-sentry-element": "Tbody",
          children: page.map(row => {
            prepareRow(row);
            const {
              key: rowKey,
              ...rowProps
            } = row.getRowProps();
            return /*#__PURE__*/_jsx(Tr, {
              ...rowProps,
              $height: "64u",
              children: row.cells.map(cell => {
                const {
                  key: cellKey,
                  ...cellProps
                } = cell.getCellProps();
                return /*#__PURE__*/_jsx(Td, {
                  $overflow: "hidden",
                  $whiteSpace: "nowrap",
                  ...cellProps,
                  children: cell.render('Cell')
                }, cellKey);
              })
            }, rowKey);
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Modal, {
      isOpen: openConfirmationModal,
      onRequestClose: () => setOpenConfirmationModal(false),
      "data-sentry-element": "Modal",
      "data-sentry-source-file": "ReportsExcludeRecords.tsx",
      "data-sentry-element": "Modal",
      children: /*#__PURE__*/_jsxs("div", {
        className: "ExcludeRecordsModal",
        children: [/*#__PURE__*/_jsx("div", {
          className: "ExcludeRecordsModal__header",
          children: "Remove reports"
        }), /*#__PURE__*/_jsxs("div", {
          className: "ExcludeRecordsModal__content",
          children: ["Do you want to remove report ID: ", /*#__PURE__*/_jsx("b", {
            children: activeRecord
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "ExcludeRecordsModal__actions",
          children: [/*#__PURE__*/_jsx(Button, {
            onClick: () => setOpenConfirmationModal(false),
            variant: "flat",
            "data-sentry-element": "Button",
            "data-sentry-source-file": "ReportsExcludeRecords.tsx",
            "data-sentry-element": "Button",
            children: "Cancel"
          }), /*#__PURE__*/_jsx(Button, {
            $ml: "2sp",
            variant: "primary",
            onClick: () => deleteExcludedRecord(activeRecord),
            "data-sentry-element": "Button",
            "data-sentry-source-file": "ReportsExcludeRecords.tsx",
            "data-sentry-element": "Button",
            children: "Confirm"
          })]
        })]
      })
    })]
  });
};
export default ReportsExcludeRecords;